import React, { useEffect, useState } from "react";
import { Modal, Platform, StyleSheet } from "react-native";
import DateTimePicker, {
  type DateTimePickerEvent,
} from "@react-native-community/datetimepicker";
import { SafeAreaView } from "react-native-safe-area-context";

import ModalHeader from "@/Components/PersonalAttributes/Components/Modal/ModalHeader";
import { toReadableText } from "@/formatting/text";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  handleSave: (date: Date | null) => void;
  initialDate: Date | null;
  maxDate: Date;
  minDate: Date;
}

function calculateDateLimit(offset: number): Date {
  const today = new Date();
  return new Date(
    today.getFullYear() - offset,
    today.getMonth(),
    today.getDate(),
  );
}

/**
 * DatePicker is a cross-platform modal date selector component.
 *
 * - On Android, it displays the native date picker as a popup.
 * - On iOS, it shows a modal with a scrollable date picker and a header.
 *
 * Props:
 * - showModal: Controls visibility of the modal or picker.
 * - closeModal: Callback to close the modal.
 * - handleSave: Callback invoked with the selected date when saved.
 * - initialDate: The initial date to display.
 * - minDate: The minimum selectable date.
 * - maxDate: The maximum selectable date.
 *
 */
function DatePicker({
  showModal,
  closeModal,
  handleSave,
  initialDate,
  minDate,
  maxDate,
}: Props): React.JSX.Element {
  // Initialize with initialDate or default to 18 years ago
  const [date, setDate] = useState(initialDate || calculateDateLimit(18));

  // Controls whether to show the native Android picker
  const [showAndroidPicker, setShowAndroidPicker] = useState(false);

  // When modal is opened, show the picker on Android if needed
  useEffect(() => {
    if (Platform.OS === "android" && showModal) {
      setShowAndroidPicker(true);
    }
  }, [showModal]);

  const onDateChange = (
    event: DateTimePickerEvent,
    selectedDate?: Date,
  ): void => {
    // Hide the picker on Android after selection
    setShowAndroidPicker(false);

    // Only update the date if the user didn't cancel (Android specific)
    if (event.type === "set" && selectedDate) {
      setDate(selectedDate);
      handleSave(selectedDate);
      closeModal();
    } else if (event.type === "dismissed") {
      closeModal();
    }
  };

  // Display Android native picker without modal header
  if (Platform.OS === "android") {
    return showAndroidPicker ? (
      <DateTimePicker
        value={date}
        mode="date"
        display="default"
        onChange={onDateChange}
        minimumDate={minDate}
        maximumDate={maxDate}
      />
    ) : (
      <></>
    );
  }

  // Display iOS modal with date picker
  return (
    <Modal
      animationType="slide"
      presentationStyle="pageSheet"
      visible={showModal}
      onRequestClose={closeModal}
    >
      <SafeAreaView style={styles.container} edges={["top"]}>
        <ModalHeader
          closeModal={closeModal}
          handleSave={() => {
            handleSave(date);
            closeModal();
          }}
          title={toReadableText(UserAttributeType.BIRTHDAY)}
        />
        <DateTimePicker
          value={date}
          mode="date"
          display="spinner"
          onChange={(event, selectedDate) => {
            if (event.type === "set" && selectedDate) {
              setDate(selectedDate);
            }
          }}
          style={styles.datePicker}
          minimumDate={minDate}
          maximumDate={maxDate}
        />
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
  },
  datePicker: {
    height: "100%",
    width: "100%",
  },
});

export default DatePicker;
